import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import api from '@/lib/api';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  ResponsiveContainer
} from 'recharts';
import {
  DollarSign, TrendingUp, Users, Building2, AlertTriangle,
  RefreshCw, Loader2, LogOut, Shield, CreditCard, BarChart3,
  HelpCircle, Send, Brain, Copy, Lock, Unlock, ChevronRight, MessageSquare, Bug, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { BrandLogoDark } from '@/components/BrandLogo';

export default function SuperAdminDashboard() {
  const [revenue, setRevenue] = useState(null);
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const navigate = useNavigate();

  // Force dark mode for super admin dashboard
  useEffect(() => {
    const root = document.documentElement;
    const prev = root.classList.contains('dark') ? 'dark' : 'light';
    root.classList.remove('light');
    root.classList.add('dark');
    return () => {
      root.classList.remove('dark');
      root.classList.add(prev);
    };
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [revRes, trendRes] = await Promise.all([
        api.get('/superadmin/revenue'),
        api.get('/superadmin/mrr-trend'),
      ]);
      setRevenue(revRes.data);
      setTrend(trendRes.data?.trend || []);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Access denied');
        navigate('/superadmin/login');
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post('/superadmin/sync-stripe');
      toast.success(`Synced ${data.synced_companies} companies from Stripe`);
      load();
    } catch { toast.error('Sync failed'); }
    finally { setSyncing(false); }
  };

  const handleLogout = () => {
    localStorage.removeItem('vs_token');
    localStorage.removeItem('vs_superadmin');
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background p-8">
        <Skeleton className="h-10 w-64 mb-8" />
        <div className="grid grid-cols-4 gap-4 mb-8">{[1,2,3,4].map(i => <Skeleton key={i} className="h-28 rounded-xl" />)}</div>
        <Skeleton className="h-80 rounded-xl" />
      </div>
    );
  }

  const kpis = [
    { label: 'Total Platform MRR', value: `$${revenue?.total_platform_mrr?.toLocaleString()}`, icon: DollarSign, color: 'text-[hsl(142,56%,36%)]', sub: `$${revenue?.total_company_mrr?.toLocaleString()} company + $${revenue?.total_vendor_paid_mrr?.toLocaleString()} vendor-paid` },
    { label: 'Active Companies', value: revenue?.active_companies, icon: Building2, color: 'text-primary', sub: `${revenue?.total_companies} total` },
    { label: 'Total Vendors', value: revenue?.total_vendors, icon: Users, color: 'text-primary', sub: `${revenue?.total_pm_paid_vendors} PM-paid, ${revenue?.total_vendor_paid_vendors} vendor-paid` },
    { label: 'Failed Payments', value: revenue?.failed_payments?.length || 0, icon: AlertTriangle, color: revenue?.failed_payments?.length > 0 ? 'text-destructive' : 'text-muted-foreground', sub: revenue?.failed_payments?.length > 0 ? 'Requires attention' : 'All healthy' },
  ];

  return (
    <div data-testid="superadmin-dashboard" className="min-h-screen bg-background">
      {/* Top Bar */}
      <header className="sticky top-0 z-30 border-b border-border bg-[hsl(222,47%,9%)]" style={{ color: 'hsl(210, 40%, 98%)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex h-14 items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <BrandLogoDark width={120} />
            <Badge variant="outline" className="text-[10px] sm:text-xs border-white/20 text-white/60 hidden sm:inline-flex">Internal</Badge>
          </div>
          <div className="flex items-center gap-1 sm:gap-3">
            <Button variant="ghost" size="sm" className="text-white/70 hover:text-white hover:bg-white/10 text-xs sm:text-sm" onClick={handleSync} disabled={syncing}>
              {syncing ? <Loader2 className="h-3 w-3 animate-spin sm:mr-2" /> : <RefreshCw className="h-3 w-3 sm:mr-2" />}
              <span className="hidden sm:inline">{syncing ? 'Syncing...' : 'Refresh Data'}</span>
            </Button>
            <Button variant="ghost" size="sm" className="text-white/70 hover:text-white hover:bg-white/10 text-xs sm:text-sm" onClick={handleLogout}>
              <LogOut className="h-3 w-3 sm:mr-2" /> <span className="hidden sm:inline">Sign Out</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <Tabs defaultValue="revenue">
          <TabsList className="mb-6">
            <TabsTrigger value="revenue"><DollarSign className="mr-2 h-4 w-4" /> Revenue</TabsTrigger>
            <TabsTrigger value="support"><HelpCircle className="mr-2 h-4 w-4" /> Support Tickets</TabsTrigger>
            <TabsTrigger value="trial-funnel"><TrendingUp className="mr-2 h-4 w-4" /> Trial Funnel</TabsTrigger>
          </TabsList>

          <TabsContent value="revenue" className="space-y-8">
        {/* Failed Payment Alerts */}
        {revenue?.failed_payments?.length > 0 && (
          <div className="space-y-2">
            {revenue.failed_payments.map((fp, i) => (
              <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-destructive/10 border border-destructive/30">
                <AlertTriangle className="h-5 w-5 text-destructive shrink-0" />
                <div>
                  <p className="text-sm font-medium text-destructive">{fp.company_name} — Payment {fp.status}</p>
                  <p className="text-xs text-destructive/70">{fp.plan_tier} plan</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* KPI Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {kpis.map((kpi, i) => (
            <Card key={i} className="rounded-xl shadow-[var(--shadow-soft)]">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{kpi.label}</p>
                    <p className="text-3xl font-bold tabular-nums" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{kpi.value}</p>
                    <p className="text-xs text-muted-foreground mt-1">{kpi.sub}</p>
                  </div>
                  <div className={`p-2 rounded-lg bg-muted ${kpi.color}`}><kpi.icon className="h-5 w-5" /></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* MRR Growth Chart */}
        <Card className="rounded-xl shadow-[var(--shadow-soft)]">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              <TrendingUp className="inline mr-2 h-4 w-4" /> MRR Growth Trend
            </CardTitle>
            <Badge variant="secondary" className="text-xs">6 months</Badge>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trend}>
                  <defs>
                    <linearGradient id="mrrGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(142, 56%, 36%)" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="hsl(142, 56%, 36%)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" tickFormatter={v => `$${v}`} />
                  <RTooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: 12 }} formatter={v => [`$${v}`, 'MRR']} />
                  <Area type="monotone" dataKey="mrr" stroke="hsl(142, 56%, 36%)" fill="url(#mrrGrad)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Revenue Heatmap Table */}
        <Card className="rounded-xl shadow-[var(--shadow-soft)]">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              <BarChart3 className="inline mr-2 h-4 w-4" /> Revenue by Company
            </CardTitle>
            <p className="text-xs text-muted-foreground">{revenue?.company_details?.length} companies</p>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="max-h-[400px]">
              <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Company</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Vendors</TableHead>
                    <TableHead className="text-right">Base Fee</TableHead>
                    <TableHead className="text-right">Vendor Fee</TableHead>
                    <TableHead className="text-right">VP MRR</TableHead>
                    <TableHead className="text-right">Total MRR</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {revenue?.company_details?.map((c, i) => {
                    const isActive = c.subscription_status === 'active';
                    const isFailed = ['past_due', 'canceled'].includes(c.subscription_status);
                    return (
                      <TableRow key={i} className={`${isFailed ? 'bg-destructive/5' : ''} hover:bg-muted/50 transition-colors duration-150`}>
                        <TableCell className="font-medium text-sm">{c.company_name}</TableCell>
                        <TableCell><Badge variant="secondary" className="text-xs capitalize">{c.plan_tier || 'none'}</Badge></TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={isActive}
                              onCheckedChange={async (checked) => {
                                try {
                                  if (checked) {
                                    await api.post(`/superadmin/activate-company/${c.company_id}`, { plan_tier: c.plan_tier || 'professional' });
                                    toast.success(`${c.company_name} activated`);
                                  } else {
                                    await api.post(`/superadmin/activate-company/${c.company_id}`, { plan_tier: c.plan_tier || 'professional', deactivate: true });
                                    toast.success(`${c.company_name} deactivated`);
                                  }
                                  load();
                                } catch { toast.error('Failed'); }
                              }}
                            />
                            <Badge variant={isActive ? 'default' : isFailed ? 'destructive' : 'outline'} className="text-xs capitalize">
                              {c.subscription_status}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell className="text-right text-sm tabular-nums">{c.total_vendors} <span className="text-muted-foreground">({c.pm_paid_vendors}+{c.vendor_paid_vendors})</span></TableCell>
                        <TableCell className="text-right text-sm tabular-nums">${c.base_fee}</TableCell>
                        <TableCell className="text-right text-sm tabular-nums">${c.pm_vendor_fee}</TableCell>
                        <TableCell className="text-right text-sm tabular-nums text-[hsl(142,56%,36%)]">${c.vendor_paid_mrr}</TableCell>
                        <TableCell className="text-right font-semibold text-sm tabular-nums" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>${c.total_mrr}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        <p className="text-xs text-center text-muted-foreground">Data as of {revenue?.calculated_at ? new Date(revenue.calculated_at).toLocaleString() : 'N/A'}</p>
          </TabsContent>

          {/* Support Tickets Tab */}
          <TabsContent value="support">
            <SuperAdminSupportTab />
          </TabsContent>

          {/* Trial Funnel Tab */}
          <TabsContent value="trial-funnel">
            <TrialFunnelTab />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// ─── SUPPORT TICKETS TAB ──────────────────────────────────────
function SuperAdminSupportTab() {
  const [tickets, setTickets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [promptResult, setPromptResult] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadTickets = useCallback(async () => {
    try {
      const { data } = await api.get('/superadmin/support/tickets');
      setTickets(data.items || []);
    } catch {}
  }, []);

  useEffect(() => { loadTickets(); }, [loadTickets]);

  // Poll for updates every 10 seconds
  useEffect(() => {
    const interval = setInterval(loadTickets, 10000);
    return () => clearInterval(interval);
  }, [loadTickets]);

  // Reload selected ticket when tickets update
  useEffect(() => {
    if (selected) {
      const updated = tickets.find(t => t.id === selected.id);
      if (updated) setSelected(updated);
    }
  }, [tickets]);

  const handleReply = async () => {
    if (!replyText.trim() || !selected) return;
    setSending(true);
    try {
      await api.post(`/support/tickets/${selected.id}/reply`, { message: replyText });
      setReplyText('');
      toast.success('Reply sent');
      loadTickets();
    } catch { toast.error('Failed to send reply'); }
    finally { setSending(false); }
  };

  const handleAiGenerate = async () => {
    if (!selected) return;
    setAiLoading(true);
    try {
      const { data } = await api.post(`/support/tickets/${selected.id}/ai-response`);
      if (data.response) { setReplyText(data.response); toast.success('AI response generated'); }
      else { toast.error(data.error || 'AI generation failed'); }
    } catch { toast.error('AI generation failed'); }
    finally { setAiLoading(false); }
  };

  const handleGeneratePrompt = async (type) => {
    if (!selected) return;
    try {
      const { data } = await api.post(`/support/tickets/${selected.id}/generate-prompt`, { type });
      setPromptResult(data);
      toast.success(`${type === 'bug' ? 'Bug report' : 'Feature request'} prompt generated`);
    } catch { toast.error('Prompt generation failed'); }
  };

  const handleStatusChange = async (status) => {
    if (!selected) return;
    try {
      await api.put(`/support/tickets/${selected.id}/status`, { status });
      toast.success(`Ticket ${status}`);
      loadTickets();
    } catch { toast.error('Failed to update status'); }
  };

  const handleDeleteTicket = async () => {
    if (!selected) return;
    if (!window.confirm(`Delete ticket "${selected.subject}"? This action cannot be undone.`)) return;
    
    setDeleting(true);
    try {
      await api.delete(`/superadmin/support/tickets/${selected.id}`);
      toast.success('Ticket deleted');
      setSelected(null);
      loadTickets();
    } catch {
      toast.error('Failed to delete ticket');
    } finally {
      setDeleting(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success('Copied to clipboard');
  };

  const priorityBadge = { urgent: 'destructive', high: 'destructive', medium: 'secondary', low: 'outline' };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      {/* Ticket List */}
      <Card className="lg:col-span-4 rounded-xl shadow-[var(--shadow-soft)]">
        <CardHeader className="pb-2">
          <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>All Tickets</CardTitle>
          <CardDescription>{tickets.length} tickets</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <ScrollArea className="h-[600px]">
            {tickets.map(t => (
              <div key={t.id} className={`p-3 border-b border-border cursor-pointer hover:bg-muted/50 transition-colors duration-150 ${selected?.id === t.id ? 'bg-muted' : ''}`} onClick={() => { setSelected(t); setPromptResult(null); }}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="text-sm font-medium truncate">{t.subject}</span>
                  <Badge variant={t.status === 'open' ? 'default' : 'outline'} className="text-[10px] shrink-0">{t.status}</Badge>
                </div>
                <p className="text-xs text-muted-foreground">{t.user_name} · {t.company_name}</p>
                <div className="flex items-center gap-1.5 mt-1">
                  <Badge variant={priorityBadge[t.priority]} className="text-[9px]">{t.priority}</Badge>
                  <span className="text-[10px] text-muted-foreground">{t.category}</span>
                  <MessageSquare className="h-3 w-3 text-muted-foreground ml-auto" />
                  <span className="text-[10px] text-muted-foreground">{t.messages?.length || 0}</span>
                </div>
              </div>
            ))}
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Ticket Detail */}
      <div className="lg:col-span-8 space-y-4">
        {selected ? (
          <>
            {/* Header */}
            <Card className="rounded-xl shadow-[var(--shadow-soft)]">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{selected.subject}</h3>
                    <p className="text-xs text-muted-foreground mt-1">{selected.user_name} · {selected.user_email} · {selected.company_name}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    {selected.status === 'open' ? (
                      <Button variant="outline" size="sm" onClick={() => handleStatusChange('closed')}><Lock className="mr-1 h-3 w-3" /> Close</Button>
                    ) : (
                      <Button variant="outline" size="sm" onClick={() => handleStatusChange('open')}><Unlock className="mr-1 h-3 w-3" /> Reopen</Button>
                    )}
                    <Button variant="destructive" size="sm" onClick={handleDeleteTicket} disabled={deleting}>
                      {deleting ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
                      Delete
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Thread */}
            <Card className="rounded-xl shadow-[var(--shadow-soft)]">
              <CardContent className="p-0">
                <ScrollArea className="h-[200px] p-4">
                  <div className="space-y-3">
                    {selected.messages?.map((msg, i) => (
                      <div key={i} className={`flex ${msg.sender === 'admin' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[80%] p-3 rounded-xl text-sm ${msg.sender === 'admin' ? 'bg-primary/10 border border-primary/20' : 'bg-muted'}`}>
                          <p className="text-xs font-medium mb-1">{msg.sender_name}</p>
                          <p className="whitespace-pre-wrap text-sm">{msg.message}</p>
                          <p className="text-[10px] text-muted-foreground mt-1">{msg.created_at ? new Date(msg.created_at).toLocaleString() : ''}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Reply Area */}
            {selected.status !== 'closed' ? (
              <Card className="rounded-xl shadow-[var(--shadow-soft)]">
                <CardContent className="p-4 space-y-3">
                  <Textarea value={replyText} onChange={e => setReplyText(e.target.value)} placeholder="Type your reply..." rows={5} className="min-h-[120px]" />
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button onClick={handleReply} disabled={sending || !replyText.trim()} size="sm">
                      {sending ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Send className="mr-1 h-3 w-3" />} Send Reply
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleAiGenerate} disabled={aiLoading}>
                      {aiLoading ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Brain className="mr-1 h-3 w-3" />} Generate AI Response
                    </Button>
                    <Separator orientation="vertical" className="h-6" />
                    <Button variant="ghost" size="sm" onClick={() => handleGeneratePrompt('bug')}><Bug className="mr-1 h-3 w-3" /> Bug Prompt</Button>
                    <Button variant="ghost" size="sm" onClick={() => handleGeneratePrompt('feature')}><Sparkles className="mr-1 h-3 w-3" /> Feature Prompt</Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="rounded-xl border-dashed"><CardContent className="p-4 text-center text-sm text-muted-foreground">Ticket is closed. Reopen to reply.</CardContent></Card>
            )}

            {/* Generated Prompt */}
            {promptResult && (
              <Card className="rounded-xl shadow-[var(--shadow-soft)]">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm">{promptResult.type === 'bug' ? 'Bug Report' : 'Feature Request'} — Emergent AI Prompt</CardTitle>
                    <Button variant="outline" size="sm" onClick={() => copyToClipboard(promptResult.prompt)}><Copy className="mr-1 h-3 w-3" /> Copy</Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <pre className="text-xs whitespace-pre-wrap bg-muted/50 p-3 rounded-lg max-h-60 overflow-auto font-mono">{promptResult.prompt}</pre>
                </CardContent>
              </Card>
            )}
          </>
        ) : (
          <Card className="rounded-xl shadow-[var(--shadow-soft)]">
            <CardContent className="py-24 text-center">
              <HelpCircle className="mx-auto h-10 w-10 text-muted-foreground/40 mb-3" />
              <p className="text-muted-foreground">Select a ticket to view details</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

const STAGE_LABELS = {
  day3: 'Day 3 · Activation',
  day10: 'Day 10 · Value recap',
  ending_5: 'Ending soon (5d)',
  ending_1: 'Last day',
  ended: 'Trial ended',
};

function TrialFunnelTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/superadmin/trial-funnel');
      setData(data);
    } catch {
      toast.error('Failed to load trial funnel');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runDrip = async () => {
    setRunning(true);
    try {
      const { data } = await api.post('/admin/trial-lifecycle/run');
      toast.success(`Lifecycle scan complete — ${data.sent} email(s) sent`);
      load();
    } catch { toast.error('Scan failed (admin only)'); }
    finally { setRunning(false); }
  };

  if (loading) {
    return <div className="grid grid-cols-4 gap-4">{[1,2,3,4].map(i => <Skeleton key={i} className="h-28 rounded-xl" />)}</div>;
  }

  const openRate = data?.email_totals?.sent ? Math.round((data.email_totals.opened / data.email_totals.sent) * 100) : 0;
  const convRate = data?.total_trials ? Math.round((data.converted / data.total_trials) * 100) : 0;

  const kpis = [
    { label: 'Total Trials', value: data?.total_trials ?? 0, icon: Users, color: 'text-primary', sub: `${data?.active_trials ?? 0} active · ${data?.expired_trials ?? 0} expired` },
    { label: 'Converted to Paid', value: data?.converted ?? 0, icon: TrendingUp, color: 'text-[hsl(142,56%,36%)]', sub: `${convRate}% of trials` },
    { label: 'Lifecycle Emails Sent', value: data?.email_totals?.sent ?? 0, icon: Send, color: 'text-primary', sub: `${data?.email_totals?.clicked ?? 0} clicked` },
    { label: 'Email Open Rate', value: `${openRate}%`, icon: BarChart3, color: 'text-primary', sub: `${data?.email_totals?.opened ?? 0} opened` },
  ];

  return (
    <div className="space-y-8" data-testid="trial-funnel-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Live view of the trial lifecycle email drip and conversions.</p>
        <Button variant="outline" size="sm" onClick={runDrip} disabled={running} data-testid="run-drip-button">
          {running ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : <RefreshCw className="mr-2 h-3 w-3" />}
          Run scan now
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((kpi, i) => (
          <Card key={i} className="rounded-xl shadow-[var(--shadow-soft)]">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{kpi.label}</p>
                  <p className="text-3xl font-bold tabular-nums" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{kpi.value}</p>
                  <p className="text-xs text-muted-foreground mt-1">{kpi.sub}</p>
                </div>
                <div className={`p-2 rounded-lg bg-muted ${kpi.color}`}><kpi.icon className="h-5 w-5" /></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="rounded-xl shadow-[var(--shadow-soft)]">
        <CardHeader className="pb-2">
          <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            <BarChart3 className="inline mr-2 h-4 w-4" /> Email engagement by stage
          </CardTitle>
          <CardDescription>How each step of the drip is performing.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Stage</TableHead>
                  <TableHead className="text-right">Reached</TableHead>
                  <TableHead className="text-right">Sent</TableHead>
                  <TableHead className="text-right">Opened</TableHead>
                  <TableHead className="text-right">Clicked</TableHead>
                  <TableHead className="text-right">Open rate</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.email_stats || []).map((row) => {
                  const reached = data.stage_reached?.find((s) => s.stage === row.stage)?.count ?? 0;
                  const rate = row.sent ? Math.round((row.opened / row.sent) * 100) : 0;
                  return (
                    <TableRow key={row.stage} data-testid={`funnel-row-${row.stage}`}>
                      <TableCell className="font-medium">{STAGE_LABELS[row.stage] || row.stage}</TableCell>
                      <TableCell className="text-right tabular-nums">{reached}</TableCell>
                      <TableCell className="text-right tabular-nums">{row.sent}</TableCell>
                      <TableCell className="text-right tabular-nums">{row.opened}</TableCell>
                      <TableCell className="text-right tabular-nums">{row.clicked}</TableCell>
                      <TableCell className="text-right tabular-nums">{rate}%</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground">
        Open/click tracking requires Resend webhook events; counts reflect what's been recorded so far.
        Data as of {data?.calculated_at ? new Date(data.calculated_at).toLocaleString() : 'N/A'}.
      </p>
    </div>
  );
}
